import React from 'react';
import { motion } from 'framer-motion';
import Navbar from './Navbar';
import GeometricBackground from './GeometricBackground';

const FeatureCard = ({ icon, title, description, benefits, delay = 0, comingSoon = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className={`glass-effect p-6 rounded-2xl hover:shadow-lg transition-all duration-300 backdrop-blur-lg bg-white/30 border border-white/20 shadow-xl group hover:-translate-y-1 relative ${
      comingSoon ? 'opacity-50' : ''
    }`}
  >
    <div className="flex items-center gap-4 mb-4">
      <div className="text-3xl bg-gradient-to-br from-[#BFEA15] to-[#D9F273] p-3 rounded-xl shadow-lg group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <h3 className="text-xl font-bold text-gray-800">{title}</h3>
    </div>
    <p className="text-gray-600 mb-4">{description}</p>
    <div className="space-y-2">
      {benefits.map((benefit, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: delay + 0.1 * index }}
          className="flex items-start gap-2"
        >
          <span className="text-[#BFEA15] mt-1">•</span>
          <span className="text-gray-600">{benefit}</span>
        </motion.div>
      ))}
    </div>
    {comingSoon && (
      <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[2px] rounded-2xl">
        <div className="bg-[#BFEA15] px-4 py-2 rounded-full text-sm font-medium text-gray-900 shadow-lg transform -rotate-12">
          Coming Soon
        </div>
      </div>
    )}
  </motion.div>
);

const Features = () => {
  const features = [
    {
      icon: "🚀",
      title: "Real-Time Portfolio Tracking",
      description: "Monitor your Solana investments with lightning-fast updates and comprehensive analytics.",
      benefits: [
        "Live price updates and portfolio valuation",
        "Transaction history with detailed categorization",
        "Performance metrics and trend analysis",
        "Custom watchlists and alerts"
      ]
    },
    {
      icon: "🤖",
      title: "AI-Powered Tax Calculations",
      description: "Leverage advanced AI algorithms for accurate tax calculations and optimization strategies.",
      benefits: [
        "Automated tax liability calculations",
        "Smart tax-loss harvesting suggestions",
        "Multi-jurisdiction tax compliance",
        "AI-generated tax optimization strategies"
      ]
    },
    {
      icon: "🔒",
      title: "Enterprise-Grade Security",
      description: "Rest easy knowing your data is protected by state-of-the-art security measures.",
      benefits: [
        "End-to-end encryption for all data",
        "Multi-factor authentication",
        "Regular security audits",
        "Cold storage integration options"
      ]
    },
    {
      icon: "📊",
      title: "Advanced Analytics",
      description: "Gain deep insights into your portfolio with sophisticated analytical tools.",
      benefits: [
        "Custom performance metrics",
        "Risk assessment tools",
        "Portfolio correlation analysis",
        "DeFi protocol tracking"
      ]
    },
    {
      icon: "📱",
      title: "Cross-Platform Access",
      description: "Access your portfolio and tax information anywhere, anytime, on any device.",
      benefits: [
        "Responsive web interface",
        "Native mobile applications",
        "Real-time synchronization",
        "Offline mode support"
      ]
    },
    {
      icon: "🔄",
      title: "Seamless Integration",
      description: "Connect with your favorite tools and services for a unified experience.",
      benefits: [
        "Major wallet integration",
        "Exchange API support",
        "Accounting software compatibility",
        "Custom API access"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="fixed inset-0">
        <GeometricBackground />
      </div>
      
      <div className="relative z-10">
        <Navbar />
        
        <main className="max-w-7xl mx-auto px-4 pt-24 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-[#BFEA15] to-[#D9F273] bg-clip-text text-transparent mb-6">
              Powerful Features
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Experience the next generation of crypto tax management with our comprehensive suite of features
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                {...feature}
                delay={0.2 + index * 0.1}
                comingSoon={index >= 3}
              />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-16 text-center"
          >
            <a
              href="/"
              className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-[#BFEA15] to-[#D9F273] rounded-xl text-gray-900 font-medium shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              Try It Now
              <span className="ml-2">→</span>
            </a>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default Features;
